import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const CENTER_X = CANVAS_WIDTH / 2;
const CENTER_Y = CANVAS_HEIGHT / 2;
const DEFAULT_BRANCHES = 5;
const DEFAULT_ITERATIONS = 6;
const DEFAULT_ANGLE_SPREAD = 25; // 각도 분산 (도)
const DEFAULT_LENGTH_DECAY = 0.78; // 길이 감소율
const MAX_ITERATIONS = 12;

const LightningTreeFractal = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [branches, setBranches] = useState(DEFAULT_BRANCHES);
  const [iterations, setIterations] = useState(DEFAULT_ITERATIONS);
  const [angleSpread, setAngleSpread] = useState(DEFAULT_ANGLE_SPREAD);
  const [lengthDecay, setLengthDecay] = useState(DEFAULT_LENGTH_DECAY);
  const [autoRotate, setAutoRotate] = useState(true);
  const [showGlow, setShowGlow] = useState(true);
  const [colorMode, setColorMode] = useState('blue'); // blue, purple, rainbow, white
  const [animation, setAnimation] = useState('grow'); // grow, pulse, static
  const rotationAngleRef = useRef(0);
  const lastUpdateTimeRef = useRef(0);
  const growthProgressRef = useRef(0);
  
  // 트리 자란 정도 (애니메이션용)
  const [growthProgress, setGrowthProgress] = useState(0);
  
  // 배경 그리기 함수
  const drawBackground = useCallback((ctx) => {
    // 밤하늘 배경 (그라데이션)
    const skyGradient = ctx.createRadialGradient(
      CENTER_X, CENTER_Y, 0,
      CENTER_X, CENTER_Y, CANVAS_WIDTH * 0.8
    );
    
    skyGradient.addColorStop(0, '#000510');
    skyGradient.addColorStop(0.5, '#000714');
    skyGradient.addColorStop(1, '#000');
    
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 별 그리기
    ctx.fillStyle = '#FFF';
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * CANVAS_WIDTH;
      const y = Math.random() * CANVAS_HEIGHT;
      const size = Math.random() * 1.2;
      
      // 중앙 근처에는 별 없음 (번개가 잘 보이도록)
      const dx = x - CENTER_X;
      const dy = y - CENTER_Y;
      const distSq = dx * dx + dy * dy;
      if (distSq < 100 * 100) continue;
      
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
      
      // 일부 별은 깜박임 효과
      if (Math.random() > 0.97) {
        ctx.beginPath();
        ctx.arc(x, y, size * 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.fill();
        ctx.fillStyle = '#FFF';
      }
    }
  }, []);
  
  // 번개 색상 가져오기
  const getLightningColor = useCallback((depth, iteration, maxIterations, angle) => {
    const normalizedDepth = depth / maxIterations;
    
    switch (colorMode) {
      case 'purple':
        // 보라색 계열
        return `rgba(180, 100, 255, ${0.9 - normalizedDepth * 0.5})`;
      
      case 'rainbow':
        // 무지개 색상 (각도에 따라 다른 색)
        const hue = ((angle + 180) / 360 * 270) % 360;
        return `hsla(${hue}, 100%, 70%, ${0.9 - normalizedDepth * 0.5})`;
      
      case 'white':
        // 흰색 계열
        return `rgba(255, 255, 255, ${0.9 - normalizedDepth * 0.5})`;
      
      default: // 'blue'
        // 파란색 계열
        return `rgba(100, 200, 255, ${0.9 - normalizedDepth * 0.5})`;
    }
  }, [colorMode]);
  
  // 번개 가지 그리기 함수
  const drawLightningBranch = useCallback((
    ctx, 
    startX, 
    startY, 
    angle, 
    length, 
    depth, 
    maxDepth, 
    currentProgress
  ) => {
    // 최대 깊이 도달 또는 애니메이션 진행 상태에 따른 중단
    if (depth >= maxDepth || currentProgress <= 0) return;
    
    // 성장 애니메이션: 현재 가지가 얼마나 자랐는지 계산
    const growthFactor = animation === 'grow' 
      ? Math.min(1, currentProgress * (maxDepth - depth + 1))
      : 1;
    
    if (growthFactor <= 0) return;
    
    // 이 가지의 실제 길이 계산 (성장 애니메이션 적용)
    const actualLength = length * growthFactor;
    
    // 번개 지그재그 효과를 위한 중간점들
    const segments = 4;
    const segmentLength = actualLength / segments;
    const points = [];
    
    // 시작점
    points.push({ x: startX, y: startY });
    
    // 중간점들
    let currentX = startX;
    let currentY = startY;
    let currentAngle = angle;
    
    for (let i = 0; i < segments; i++) {
      // 지그재그 각도 변화 (진행 방향과 수직인 방향으로 랜덤하게 변위)
      const perpAngle = currentAngle + Math.PI / 2;
      const deviation = (Math.random() * 2 - 1) * 0.3; // -0.3 ~ 0.3
      
      currentX += Math.cos(currentAngle) * segmentLength;
      currentY += Math.sin(currentAngle) * segmentLength;
      
      // 지그재그 편차 적용
      currentX += Math.cos(perpAngle) * segmentLength * deviation;
      currentY += Math.sin(perpAngle) * segmentLength * deviation;
      
      // 각도 약간 변경 (번개의 방향 변화)
      currentAngle += (Math.random() * 0.4 - 0.2);
      
      points.push({ x: currentX, y: currentY });
    }
    
    // 번개 색상 (깊이에 따라 변화)
    const mainColor = getLightningColor(depth, depth, maxDepth, angle * 180 / Math.PI);
    
    // 발광 효과 (옵션에 따라)
    if (showGlow) {
      ctx.shadowBlur = 15;
      ctx.shadowColor = mainColor;
    }
    
    // 번개 그리기
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = 3 - depth * 0.2;
    ctx.stroke();
    
    // 발광 효과 끄기
    if (showGlow) {
      ctx.shadowBlur = 0;
    }
    
    // 마지막 점은 다음 가지의 시작점
    const endX = points[points.length - 1].x;
    const endY = points[points.length - 1].y;
    
    // 다음 깊이의 가지 길이
    const nextLength = length * lengthDecay;
    
    // 펄스 애니메이션: 다음 가지의 진행 상태 계산
    // 가지가 뻗어나가는 효과가 위에서 아래로 흐르도록
    let nextProgress = currentProgress;
    if (animation === 'pulse') {
      const pulseSpeed = 2.5;
      nextProgress = (currentProgress - 0.1 * pulseSpeed);
    }
    
    // 새 가지들 그리기 (각 가지는 angleSpread 내에서 랜덤하게 각도 결정)
    const branchCount = depth === 0 ? branches : 2;
    const angleStep = (Math.PI * 2) / branchCount;
    
    for (let i = 0; i < branchCount; i++) {
      // 첫 번째 깊이(메인 가지들)는 균등하게 분포, 그 이후는 랜덤 분기
      let newAngle;
      
      if (depth === 0) {
        // 메인 가지: 균등 분포 (회전 적용)
        newAngle = angle + angleStep * i;
      } else {
        // 하위 가지: 각도 랜덤 분산
        const spread = (angleSpread * Math.PI / 180) / depth; // 깊이가 깊을수록 분산 줄어듦
        newAngle = angle + (Math.random() * spread * 2 - spread);
      }
      
      // 재귀 호출하여 다음 가지 그리기
      drawLightningBranch(
        ctx,
        endX,
        endY,
        newAngle,
        nextLength,
        depth + 1,
        maxDepth,
        nextProgress
      );
    }
  }, [showGlow, lengthDecay, angleSpread, branches, animation, getLightningColor]);
  
  // 프랙탈 트리 그리기
  const drawLightningTree = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 배경 그리기
    drawBackground(ctx);
    
    // 애니메이션 시간 계산
    const deltaTime = lastUpdateTimeRef.current ? (timestamp - lastUpdateTimeRef.current) / 1000 : 0;
    lastUpdateTimeRef.current = timestamp;
    
    // 자동 회전 업데이트
    if (autoRotate) {
      rotationAngleRef.current += deltaTime * 0.2; // 회전 속도
    }
    
    // 성장 애니메이션 업데이트
    if (animation === 'grow') {
      if (growthProgressRef.current < 1) {
        growthProgressRef.current += deltaTime * 0.5; // 성장 속도
        if (growthProgressRef.current > 1) growthProgressRef.current = 1;
        setGrowthProgress(growthProgressRef.current);
      }
    } else if (animation === 'pulse') {
      // 펄스 애니메이션은 계속해서 0.2 ~ 1.0 사이를 순환
      growthProgressRef.current = 0.2 + 0.8 * (0.5 + 0.5 * Math.sin(timestamp * 0.001));
      setGrowthProgress(growthProgressRef.current);
    } else {
      // 정적 모드
      growthProgressRef.current = 1;
      setGrowthProgress(1);
    }
    
    // 중앙에서 시작하는 메인 가지 그리기
    // 기준 각도 (자동 회전 적용)
    const baseAngle = rotationAngleRef.current;
    
    // 초기 길이 (캔버스 크기의 일정 비율)
    const initialLength = Math.min(CANVAS_WIDTH, CANVAS_HEIGHT) * 0.2;
    
    // 번개 트리 그리기
    drawLightningBranch(
      ctx,
      CENTER_X,
      CENTER_Y,
      baseAngle,
      initialLength,
      0,
      iterations,
      growthProgressRef.current
    );
    
    // 중앙의 발광 효과 (번개의 근원지)
    const glowRadius = 20 + 5 * Math.sin(timestamp * 0.002);
    
    if (showGlow) {
      const gradient = ctx.createRadialGradient(
        CENTER_X, CENTER_Y, 0,
        CENTER_X, CENTER_Y, glowRadius
      );
      
      let glowColor;
      switch (colorMode) {
        case 'purple': glowColor = 'rgba(180, 100, 255, 0.8)'; break;
        case 'rainbow': glowColor = 'rgba(255, 200, 100, 0.8)'; break;
        case 'white': glowColor = 'rgba(255, 255, 255, 0.8)'; break;
        default: glowColor = 'rgba(100, 200, 255, 0.8)'; // blue
      }
      
      gradient.addColorStop(0, glowColor);
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(CENTER_X, CENTER_Y, glowRadius, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // 다음 프레임 요청
    animationRef.current = requestAnimationFrame(drawLightningTree);
  }, [drawBackground, drawLightningBranch, iterations, autoRotate, showGlow, animation]);
  
  // 초기화 및 변경 시 렌더링
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    // 애니메이션 시작
    lastUpdateTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(drawLightningTree);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [drawLightningTree]);
  
  // 설정 변경 핸들러들
  const handleBranchesChange = (e) => {
    setBranches(parseInt(e.target.value));
  };
  
  const handleIterationsChange = (e) => {
    setIterations(parseInt(e.target.value));
  };
  
  const handleAngleSpreadChange = (e) => {
    setAngleSpread(parseInt(e.target.value));
  };
  
  const handleLengthDecayChange = (e) => {
    setLengthDecay(parseFloat(e.target.value));
  };
  
  const toggleAutoRotate = () => {
    setAutoRotate(!autoRotate);
  };
  
  const toggleGlow = () => {
    setShowGlow(!showGlow);
  };
  
  const setAnimationMode = (mode) => {
    setAnimation(mode);
    
    // 성장 애니메이션으로 변경 시 처음부터 시작
    if (mode === 'grow') {
      growthProgressRef.current = 0;
      setGrowthProgress(0);
    }
  };
  
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>번개 가지 프랙탈</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label htmlFor="branches" style={labelStyle}>메인 가지 수: {branches}</label>
          <input
            id="branches"
            type="range"
            min="3"
            max="12"
            value={branches}
            onChange={handleBranchesChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="iterations" style={labelStyle}>반복 깊이: {iterations}</label>
          <input
            id="iterations"
            type="range"
            min="1"
            max={MAX_ITERATIONS}
            value={iterations}
            onChange={handleIterationsChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="angleSpread" style={labelStyle}>각도 분산: {angleSpread}°</label>
          <input
            id="angleSpread"
            type="range"
            min="5"
            max="60"
            value={angleSpread}
            onChange={handleAngleSpreadChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label htmlFor="lengthDecay" style={labelStyle}>길이 감소율: {lengthDecay.toFixed(2)}</label>
          <input
            id="lengthDecay"
            type="range"
            min="0.6"
            max="0.9"
            step="0.01"
            value={lengthDecay}
            onChange={handleLengthDecayChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <label style={labelStyle}>색상 모드</label>
          <div style={buttonGroupStyle}>
            <button
              onClick={() => setColorMode('blue')}
              style={{
                ...colorButtonStyle,
                backgroundColor: colorMode === 'blue' ? '#00aaff' : '#333'
              }}
            >
              파란색
            </button>
            <button
              onClick={() => setColorMode('purple')}
              style={{
                ...colorButtonStyle,
                backgroundColor: colorMode === 'purple' ? '#9966ff' : '#333'
              }}
            >
              보라색
            </button>
            <button
              onClick={() => setColorMode('rainbow')}
              style={{
                ...colorButtonStyle,
                background: colorMode === 'rainbow' 
                  ? 'linear-gradient(90deg, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff)'
                  : '#333'
              }}
            >
              무지개
            </button>
            <button
              onClick={() => setColorMode('white')}
              style={{
                ...colorButtonStyle,
                backgroundColor: colorMode === 'white' ? '#fff' : '#333',
                color: colorMode === 'white' ? '#000' : '#fff'
              }}
            >
              흰색
            </button>
          </div>
        </div>
        
        <div style={controlGroupStyle}>
          <label style={labelStyle}>애니메이션 모드</label>
          <div style={buttonGroupStyle}>
            <button
              onClick={() => setAnimationMode('static')}
              style={{
                ...animButtonStyle,
                backgroundColor: animation === 'static' ? '#555' : '#333'
              }}
            >
              정적
            </button>
            <button
              onClick={() => setAnimationMode('grow')}
              style={{
                ...animButtonStyle,
                backgroundColor: animation === 'grow' ? '#555' : '#333'
              }}
            >
              성장
            </button>
            <button
              onClick={() => setAnimationMode('pulse')}
              style={{
                ...animButtonStyle,
                backgroundColor: animation === 'pulse' ? '#555' : '#333'
              }}
            >
              파동
            </button>
          </div>
        </div>
        
        <div style={toggleContainerStyle}>
          <button
            onClick={toggleAutoRotate}
            style={{
              ...toggleButtonStyle,
              backgroundColor: autoRotate ? '#00aaff' : '#333'
            }}
          >
            자동 회전 {autoRotate ? 'ON' : 'OFF'}
          </button>
          
          <button
            onClick={toggleGlow}
            style={{
              ...toggleButtonStyle,
              backgroundColor: showGlow ? '#00aaff' : '#333'
            }}
          >
            발광 효과 {showGlow ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #050a14 0%, #0a1425 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(0,0,0,0.6)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.05) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  marginTop: '1.5rem',
  width: '80%',
  maxWidth: '500px',
  background: 'rgba(5, 20, 40, 0.7)',
  padding: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#aadfff'
};

const sliderStyle = {
  width: '100%',
  height: '8px',
  appearance: 'none',
  background: 'linear-gradient(to right, #081830, #0a4a8b)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer'
};

const buttonGroupStyle = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap'
};

const colorButtonStyle = {
  padding: '0.5rem',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  flex: '1 1 auto',
  minWidth: '70px',
  color: '#fff',
  fontWeight: 'bold',
  transition: 'background 0.2s'
};

const animButtonStyle = {
  padding: '0.5rem',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  flex: '1 1 auto',
  minWidth: '70px',
  color: '#fff',
  fontWeight: 'bold',
  transition: 'background 0.2s'
};

const toggleContainerStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '0.5rem'
};

const toggleButtonStyle = {
  padding: '0.75rem',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  flex: '1 1 auto',
  color: '#fff',
  fontWeight: 'bold',
  transition: 'background 0.2s'
};

export default LightningTreeFractal; 